import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { createUseStyles } from 'react-jss';
import { primaryButtonStyle } from '../constants/ButtonStyles';
import { IDLE_MODAL_COUNTDOWN_SECONDS } from '../constants/app';

const useStyles = createUseStyles({
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    padding: '12px 16px',
  },
  content: {
    padding: '8px 16px',
  },
  dialogPaper: {
    '@media (max-width: 768px)': {
      padding: 12,
      width: '95%',
    },
    maxWidth: '500px',
    padding: 16,
    width: '90%',
  },
  logoutButton: {
    borderColor: '#ccc !important',
    color: '#666 !important',
    fontSize: '14px',
    minWidth: '140px',
    padding: '8px 16px',
  },
  primaryButton: {
    backgroundColor: (primaryButtonStyle as any).backgroundColor || '#1976d2',
    color: (primaryButtonStyle as any).color || '#fff',
    fontSize: '14px',
    minWidth: '140px',
    padding: '8px 16px',
  },
  title: {
    padding: '16px 16px 8px 16px',
  },
});

interface IdleTimeoutModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when user clicks continue */
  onContinue: () => void;
  /** Optional callback when user clicks logout */
  onLogout?: () => void;
  /** Optional callback when countdown reaches zero */
  onCountdownEnd?: () => void;
  /** Countdown duration in seconds */
  countdownSeconds?: number;
  /** Optional custom title */
  title?: string;
  /** Optional custom message */
  message?: string;
}

/**
 * Modal component that displays when user is idle
 */
const IdleTimeoutModal: React.FC<IdleTimeoutModalProps> = ({
  open,
  onContinue,
  onLogout,
  onCountdownEnd,
  countdownSeconds = IDLE_MODAL_COUNTDOWN_SECONDS,
  title = 'Are you still there?',
  message = "You've been inactive for a while. Would you like to continue your session?",
}) => {
  const classes = useStyles();
  const [timeLeft, setTimeLeft] = useState(countdownSeconds);

  useEffect(() => {
    if (!open) {
      setTimeLeft(countdownSeconds);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (onCountdownEnd) {
            onCountdownEnd();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, countdownSeconds, onCountdownEnd]);

  // Format time left as minutes and seconds
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;

    if (minutes > 0) {
      return `${minutes} min ${seconds} sec`;
    }
    return `${seconds} sec`;
  };

  return (
    <Dialog
      open={open}
      onClose={onContinue}
      maxWidth={false}
      PaperProps={{
        className: classes.dialogPaper,
      }}
      aria-labelledby="idle-timeout-dialog-title"
      disableEscapeKeyDown={false}
    >
      <DialogTitle id="idle-timeout-dialog-title" className={classes.title}>
        {title}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Typography variant="body1" sx={{ marginBottom: 1.5 }}>
          {message}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: timeLeft <= 5 ? '#d32f2f' : '#666',
            fontSize: '0.9rem',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          Your session will be automatically signed out in {formatTimeLeft()}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.actions}>
        {onLogout && (
          <Button
            onClick={onLogout}
            variant="outlined"
            className={classes.logoutButton}
          >
            Logout
          </Button>
        )}
        <Button
          onClick={onContinue}
          variant="contained"
          className={classes.primaryButton}
          autoFocus
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutModal;
